import { Dayjs } from 'dayjs';
import { TRANSPORT_TYPES, TRANSPORT_FORM_TYPES } from '../../constants';
import { ITripDateMismatchError } from '../../types';
import { AddressOutput } from 'utils/types';

export interface ITransportationVoucherTraveler {
  passengerName: string | null;
  ticketNumber: string | null;
  seatNumber: string | null;
  cabinClass: string | null;
  passengerDOB: string | null;
  passportNumber: string | null;
  maxCheckedBaggageNumber: number | null;
  maxCheckedBaggageWeight: number | null;
  maxCabinBaggageNumber: number | null;
  maxCabinBaggageWeight: number | null;
  matched: boolean;
}

export interface ITransportationVoucherDetails {
  companyType: string | null;
  bookingNumber: string;
  voucherUrls?: string[];
  salesCost?: number;
  totalCost: number | null;
  totalCostCurrency: string;
  totalCostCurrencyIso: string;
  paymentMethod: string;
  companyName: string;
  departureCity: string;
  departureStation: string;
  departureDateTime: string;
  departurePlatform: string | null;
  arrivalCity: string | null;
  arrivalStation: string | null;
  arrivalDateTime: string;
  arrivalPlatform: string | null;
  travelers: ITransportationVoucherTraveler[];
  referenceDocumentUrl: string;
  totalJourneyTime: string;
}

export interface IParsedTransportVoucherResponse {
  success: boolean;
  message: string;
  data: {
    documentId: number;
    tripDateMismatchError: ITripDateMismatchError;
    missingTravelerError: string;
    documentDetails: ITransportationVoucherDetails;
  };
  status: number;
}

export interface ITransportStation extends AddressOutput {
  cityId: number;
  cityName: string;
}
export interface TransportLegs {
  id: number;
  legNumber?: number;
  companyName: string;
  transportationNumber: string;
  departureStation: ITransportStation;
  departureDateTime: string;
  departurePlatform: string;
  gateNumber: string;
  zone: string;
  arrivalStation: ITransportStation;
  arrivalDateTime: string;
  arrivalPlatform: string;
  travelers:
    | {
        passengerName: string;
        passengerDob: string;
        passportNumber: string;
        ticketNumber: string;
        seatNumber: string;
        cabinClass: string;
        maxCheckedBaggageNumber: number;
        maxCheckedBaggageWeight: number;
        maxCabinBaggageNumber: number;
        maxCabinBaggageWeight: number;
        travelerId: number | null;
        boardingPassUrl?: string;
      }[]
    | [];
}
export interface ICreateTransportRequest {
  // transport data
  tripId: number;
  agentId?: number;
  documentId?: number | null;
  companyType: TRANSPORT_TYPES | null;
  bookingId?: number;
  bookingNumber: string | null;
  voucherUrls?: string[];
  salesCost: number | string;
  totalCost: number;
  currencyCode: string;
  currencyIso: string;
  paymentMethod: string;
  bookingSource: string;
  contactDetails: string;
  additionalNotes: string;
  isNewBooking?: boolean;
  documentDate?: string | null;
  isUploaded?: boolean;
  // legs data
  transportLegs: TransportLegs[];
}

export interface ITransportTraveler {
  id: number;
  passengerName: string;
  dob: string;
  passportNumber: string;
  ticketNumber: string;
  seatNumber: string;
  cabinClass: string;
  maxCheckedBaggageNumber: number;
  maxCheckedBaggageWeight: number;
  maxCabinBaggageNumber: number;
  maxCabinBaggageWeight: number;
  boardingPassUrl?: string;
  travelerName?: string;
  travelerId?: number | null;
  photo: string;
  hasConsent?: boolean;
  consentRevokedAt?: string;
}

export interface ITransportLeg {
  id: number;
  legNumber: number;
  companyName: string;
  transportationNumber: string;
  layoverTime: number;
  departureStation: ITransportStation;
  departurePlatform: string;
  gateNumber: string;
  zone: string;
  departureDateTime: string | undefined;
  arrivalStation: ITransportStation;
  arrivalDateTime: string | undefined;
  arrivalPlatform: string;
  duration?: number;
  travelers: ITransportTraveler[];
}
export interface ITransport {
  tripId: number;
  agentId?: number;
  bookingId?: number;
  companyType: string;
  transportType: TRANSPORT_TYPES;
  bookingNumber: string;
  voucherUrls?: string[];
  salesCost?: number;
  totalCost: number;
  currencyCode: string;
  currencyIso: string;
  totalCostCurrency: string;
  totalCostCurrencyIso: string;
  paymentMethod: string;
  bookingSource: string;
  contactDetails: string;
  additionalNotes: string;
  documentDate?: string;
  referenceDocumentUrl?: string;
  // legs data
  transportLegs: ITransportLeg[];
  // warnings from document parsing
  warnings?: {
    bookingReferenceWarning?: string | null | undefined;
    ticketNumberWarning?: string | null | undefined;
    transportationNumberWarning?: string | null | undefined;
  };
}

export interface ITransportFormData {
  id?: number;
  tripId?: number;
  agentId?: number;
  companyType?: TRANSPORT_TYPES;
  companyName?: string;
  bookingNumber?: string;
  departureCity?: string;
  departureStation?: string;
  departureDatetime?: string;
  departurePlatform?: string;
  arrivalCity?: string;
  arrivalStation?: string;
  arrivalDatetime?: string;
  arrivalPlatform?: string;
  salesCost?: number | string;
  totalCost?: number | string;
  currencyCode?: string;
  paymentMethod?: string;
  bookingSource?: string;
  contactDetails?: string;
  additionalNotes?: string;
  totalJourneyTime?: string;
  travelers?: ITransportTraveler[];
  transportTravelers?: { [key: string]: any };
}

export interface ITransportFormValues {
  companyName: string;
  bookingId?: number;
  bookingNumber: string;
  voucherUrls?: string[];
  departureCity: string;
  departureCityDetails: ICity;
  departureStationDetails: AddressOutput;
  departureDate: Dayjs;
  departureTime: Dayjs;
  departurePlatform?: string;
  gateNumber?: string;
  zone?: string;
  arrivalCity: string;
  arrivalCityDetails: ICity;
  arrivalStationDetails: AddressOutput;
  arrivalDate: Dayjs;
  arrivalTime: Dayjs;
  arrivalPlatform?: string;
  salesCost?: number | string;
  totalCost: {
    amount: number;
    currency: string;
    currencyIso: string;
  };
  paymentMethod: string;
  bookingSource?: string;
  contactDetails?: string;
  additionalNotes?: string;
  transportationNumber?: string;
  transportTravelers?: {
    [key: string]: unknown;
  };
}

export interface TransportFormProps {
  visible: boolean;
  onClose: () => void;
  transportData?: ITransportDetails;
  transportLegData?: ITransportLeg;
  setTransportData?: React.Dispatch<React.SetStateAction<ITransport>>;
  formType: TRANSPORT_FORM_TYPES;
  agencyId: string | number;
  tripId: string;
  readOnly?: boolean;
  serviceType?: TRANSPORT_TYPES | null;
  showTransportFormReviewModal?: boolean;
  missingFields?: RequiredFieldsMissingData;
  onFieldFilled?: (fieldName: string, legIndex?: number) => void;
  currentLegIndex?: number;
  onFormValidationChange?: (hasErrors: boolean) => void;
  canApply?: () => boolean;
  onApply?: () => void;
}

export interface ITransportBoardingPass {
  success: boolean;
  message: string;
  data: ITransport;
  status: number;
}

export interface IGetTransportsResponse {
  success: boolean;
  data: ITransport[];
  message: string;
  status: number;
}

export interface IUpdateTransportRequest extends ICreateTransportRequest {}

export interface IRemoveTransportBoardingPassResponse {
  success: boolean;
  message: string;
  status: number;
}

export interface ITransportDetails {
  tripId: number;
  agentId?: number;
  companyType: string;
  transportType: TRANSPORT_TYPES;
  bookingId?: number;
  bookingNumber: string;
  voucherUrls?: string[];
  salesCost?: number;
  totalCost: number;
  currencyCode: string;
  currencyIso: string;
  totalCostCurrency: string;
  totalCostCurrencyIso: string;
  paymentMethod: string;
  bookingSource: string;
  contactDetails: string;
  additionalNotes: string;
  documentDate?: string;
  referenceDocumentUrl?: string;
}

export interface RequiredFieldsMissingData {
  globalFields?: string[];
  legLevel?: {
    [key: string]: string[];
  };
}

export interface TransportCardProps {
  transportDetails: ITransportDetails;
  transportLegDetails: ITransportLeg;
  isListing?: boolean;
  className?: string;
  agencyId: string | number;
  tripId: string;
  setTransportData?: React.Dispatch<React.SetStateAction<ITransport | undefined>>;
  serviceType?: TRANSPORT_TYPES | null;
  setShowTransportFormReviewModal?: (show: boolean) => void;
  onEdit?: () => void;
  showEditButton?: boolean;
  searchMode?: boolean;
  selectedTravelerIds?: number[];
  requiredFieldsMissing?: boolean | RequiredFieldsMissingData;
  legIndex?: number;
}

export interface PassengerProps {
  name: string;
  eTicket: string;
  seatNumber: string;
  avatarSrc: string;
  boardingPass: string;
  travelerId?: number;
}

export interface IIdNameResponse {
  id: number;
  name: string;
}

export interface ICity {
  id: number;
  name: string;
  country: IIdNameResponse;
  state: IIdNameResponse;
}

export interface IGetCitiesResponse {
  success: boolean;
  data: ICity[];
  message: string;
  status: number;
}

export interface ICityForDuration {
  id: number | null;
  name: string | null;
  country: string | null;
  state: string | null;
}

export interface ICalculateDurationRequest {
  arrivalCity: ICityForDuration;
  departureCity: ICityForDuration;
  departureDateTime: string;
  arrivalDateTime: string;
}

export interface ICalculateDurationResponse {
  success: boolean;
  data: {
    duration: number;
    departureCity: ICity;
    arrivalCity: ICity;
  };
  message: string;
  status: number;
}

export const enum TransportStationPlaceType {
  BUS_STOP = 'BUS_STOP',
  TRAIN_STATION = 'TRAIN_STATION',
  FERRY_PORT = 'FERRY_PORT',
}
